import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../components/ui/badge';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import type { ReportingRun } from '../../types/reporting';
import { toLocalYmd } from '../../lib/reportingDateRange';
import { isGa4RunMode } from '../../lib/reporting/runModes';
import {
  demographicsFreshnessMessage,
  resolveDemographicsFreshnessNotice,
} from '../../lib/reporting/demographicsFreshness';
import { getReportingSyncHealth } from './api';
import { RefreshZernioProfiles } from './RefreshZernioProfiles';
import {
  formatRelativeAge,
  formatRunDuration,
  formatSkipBreakdown,
  freshnessLevel,
  hasActionableSkips,
  isConcerningSkipReason,
  hasMeasuredCounts,
  runStatusChip,
  skipReasonLabel,
  type FreshnessLevel,
} from './syncHealthView';

// Sync Health page (/reporting/sync-health, admin-only). The freshness banner is
// the single "is the pipeline alive" signal; the runs table surfaces per-account
// (partial) failures the Cloud Run execution status hides. Polls every 45s (runs
// are 2-hourly, so no need for aggressive polling).

const BANNER_STYLE: Record<FreshnessLevel, string> = {
  fresh: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  stale: 'border-amber-200 bg-amber-50 text-amber-800',
  dead: 'border-rose-200 bg-rose-50 text-rose-800',
  unknown: 'border-slate-200 bg-slate-50 text-slate-700',
};

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * A YYYY-MM-DD as a plain date.
 *
 * ⚠️ NOT `fmtDateTime`, which takes an INSTANT — passing a bare date to it reads
 * back as midnight UTC in the reader's zone, which is the wrong DAY for anyone
 * east of Greenwich. The demographics signal is day-grained by nature.
 */
function fmtDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-AU', { dateStyle: 'medium', timeZone: 'UTC' });
}

function fmtWindow(run: ReportingRun): string {
  if (!run.windowFrom && !run.windowTo) return '—';
  return `${run.windowFrom ?? '?'} → ${run.windowTo ?? '?'}`;
}

export function SyncHealthPage() {
  const me = useCurrentUser();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [skipExpanded, setSkipExpanded] = useState<Set<string>>(new Set());
  const [deferredExpanded, setDeferredExpanded] = useState<Set<string>>(new Set());
  const [callsExpanded, setCallsExpanded] = useState<Set<string>>(new Set());

  const health = useQuery({
    queryKey: ['reporting', 'sync-health'],
    queryFn: getReportingSyncHealth,
    enabled: me.role === 'admin',
    refetchInterval: 45_000, // runs are 2-hourly; a gentle poll keeps freshness live
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view reporting sync health.</p>;
  }

  const data = health.data;
  const now = Date.now();
  const level = freshnessLevel(data?.hoursStale ?? null);
  const age = formatRelativeAge(data?.lastSuccessfulAt ?? null, now);
  // The latest fully-successful run drives the banner — surface its run-length
  // too so pull duration is visible at a glance (same run buildSyncHealth used).
  const lastSuccessfulRun =
    // ⚠️ Meta runs only — the same exclusion resolveDataFreshness applies, or a
    // GA4 run would set the banner's duration beside a Meta freshness stamp.
    data?.runs.find((r) => !isGa4RunMode(r.mode) && r.finishedAt !== null && r.failedCount === 0) ?? null;
  // ⚠️ The VIEWER'S local today (`toLocalYmd`), not UTC — this container runs on
  // UTC and the reader does not, so deriving the age server-side would report a
  // Brisbane admin a day out every morning before 10:00.
  const demographicsNotice = resolveDemographicsFreshnessNotice({
    today: toLocalYmd(new Date()),
    freshness: data?.demographics,
    unavailable: Boolean(health.error),
  });
  const lastDuration = lastSuccessfulRun
    ? formatRunDuration(lastSuccessfulRun.startedAt, lastSuccessfulRun.finishedAt)
    : '—';

  const toggle = (runId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  const toggleSkip = (runId: string) =>
    setSkipExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  const toggleCalls = (runId: string) =>
    setCallsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  const toggleDeferred = (runId: string) =>
    setDeferredExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  return (
    <div>
      <PageHeader
        title="Sync health"
        description="Every reporting pull run — status, per-account failures, and data freshness. The banner is the catch-all 'is the pipeline alive' signal."
        backHref="/reporting"
      />

      <RefreshZernioProfiles />

      {/* Freshness banner — the single most valuable signal. */}
      <div className={`mb-6 rounded-md border px-4 py-3 text-sm ${BANNER_STYLE[level]}`}>
        {data === undefined ? (
          <span>Loading sync status…</span>
        ) : data.lastSuccessfulAt === null ? (
          <span className="font-medium">No successful sync recorded yet.</span>
        ) : (
          <span>
            <span className="font-medium">Last successful sync: {age ?? '—'}</span>
            {lastDuration !== '—' && <span className="text-current/70"> · took {lastDuration}</span>}
            <span className="text-current/70"> ({fmtDateTime(data.lastSuccessfulAt)})</span>
            {level !== 'fresh' && (
              <span className="ml-2 font-medium">
                — {level === 'dead' ? 'the pipeline may be down' : 'sync is running behind'}.
              </span>
            )}
          </span>
        )}
        {/* ⚠️⚠️ NAMES THE CAUSE WHERE THE READER ALREADY IS. The line above
            only ever says the sync is BEHIND, which is the symptom of a dead
            scheduler and of a crashing job alike — and on 2026-09-22 it was read
            as the first when it was the second. "Started and never finished" is
            the distinction, and it belongs in the banner rather than one glance
            further down the table, because the banner is where someone looks
            when they think the pull has stopped. */}
        {(data?.diedRunCount ?? 0) > 0 && (
          <div className="mt-1 font-medium">
            {data!.diedRunCount} recent run{data!.diedRunCount === 1 ? '' : 's'} started and never
            finished — the job is firing and dying, not failing to fire. See the run list below,
            then the Cloud Run execution logs for the cause.
          </div>
        )}
      </div>

      {/* ⚠️⚠️ A SECOND BANNER, BECAUSE IT IS A SECOND JOB. The banner above reads
          `reporting_runs`, which describes the 2-hourly DAILY pull. The nightly
          AUDIENCE pass is a separate Cloud Run job on a separate schedule — and
          on 2026-08-31 it had been failing on every fire (a scheduler created
          without run.invoker) while the banner above stayed green. Folding the
          two into one signal is exactly how that hid. */}
      {demographicsNotice ? (
        <div
          className={`mb-6 rounded-md border px-4 py-3 text-sm ${
            demographicsNotice.kind === 'stale' || demographicsNotice.kind === 'never'
              ? BANNER_STYLE.stale
              : BANNER_STYLE.fresh
          }`}
          data-testid="demographics-freshness-banner"
        >
          {demographicsFreshnessMessage(demographicsNotice, fmtDay)}
        </div>
      ) : null}

      {health.isLoading ? (
        <CenteredSpinner />
      ) : health.error ? (
        <p className="text-sm text-red-600">Couldn't load sync health.</p>
      ) : (data?.runs.length ?? 0) === 0 ? (
        <p className="text-sm text-slate-600">
          No runs recorded yet. The next scheduled pull (or a manual run) will populate this.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">Mode</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium text-right">Ok / Fail / Skip</th>
                <th className="px-4 py-2 font-medium text-right">Rows (daily / demo)</th>
                <th className="px-4 py-2 font-medium">Window</th>
                <th className="px-4 py-2 font-medium text-center">Reconcile</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.runs ?? []).map((run) => {
                const chip = runStatusChip(run, now);
                const measured = hasMeasuredCounts(run);
                const hasFailures = run.failedAccounts.length > 0;
                // ⚠️ GA4 runs report per-REQUEST failures inside a property that
                // still succeeded. The count is the witness: null = not reported.
                const callFailureCount = run.callFailureCount ?? 0;
                const callsOpen = callsExpanded.has(run.runId);
                const ga4 = isGa4RunMode(run.mode);
                const hasDeferred = run.deferredAccounts.length > 0;
                const deferredOpen = deferredExpanded.has(run.runId);
                const isOpen = expanded.has(run.runId);
                // Skip detail exists when the run recorded skips (breakdown non-
                // null AND at least one skip). Legacy rows (null breakdown) render
                // the bare number with no expander.
                const hasSkipDetail = run.skipBreakdown !== null && run.skippedCount > 0;
                const skipOpen = skipExpanded.has(run.runId);
                return (
                  <Fragment key={run.runId}>
                    <tr className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-600" title={run.runId}>
                        {fmtDateTime(run.startedAt)}
                      </td>
                      <td className="px-4 py-2 tabular-nums text-slate-600">
                        {formatRunDuration(run.startedAt, run.finishedAt)}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{run.mode ?? '—'}</td>
                      <td className="px-4 py-2">
                        <Badge variant={chip.variant}>{chip.label}</Badge>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {!measured ? (
                          // ⚠️ Dashes, never zeroes — see `hasMeasuredCounts`.
                          <span className="text-slate-400" title="This run has not recorded a finish, so its counts were never written.">
                            &mdash; / &mdash; / &mdash;
                          </span>
                        ) : (
                          <>
                        {run.succeededCount} /{' '}
                        <span className={run.failedCount > 0 ? 'text-rose-600 font-medium' : ''}>
                          {run.failedCount}
                        </span>{' '}
                        /{' '}
                        {hasSkipDetail ? (
                          <button
                            type="button"
                            title={run.skipBreakdown ? formatSkipBreakdown(run.skipBreakdown) : undefined}
                            onClick={() => toggleSkip(run.runId)}
                            className={`underline decoration-dotted underline-offset-2 hover:text-slate-900 ${
                              hasActionableSkips(run) ? 'text-amber-700 font-medium' : 'text-slate-600'
                            }`}
                          >
                            {run.skippedCount}
                          </button>
                        ) : (
                          run.skippedCount
                        )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700">
                        {measured ? (
                          `${run.dailyRows.toLocaleString()} / ${run.demographicsRows.toLocaleString()}`
                        ) : (
                          <span className="text-slate-400">&mdash; / &mdash;</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-500">{fmtWindow(run)}</td>
                      <td className="px-4 py-2 text-center">
                        {ga4 ? (
                          <span className="text-slate-400" title="GA4 runs have no leads to reconcile">&mdash;</span>
                        ) : run.leadsReconcile ? (
                          <span className="text-emerald-600" title="Σmeta + Σwebsite == Σtotal">✓</span>
                        ) : (
                          <span className="text-rose-600" title="lead reconciliation failed">✗</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-3">
                          {hasDeferred && (
                            <button
                              type="button"
                              className="text-xs text-amber-700 underline hover:text-amber-900"
                              onClick={() => toggleDeferred(run.runId)}
                              title="Still ingesting at the poll cap — re-run later"
                            >
                              {deferredOpen ? 'Hide' : `${run.deferredAccounts.length} deferred`}
                            </button>
                          )}
                          {callFailureCount > 0 && (
                            <button
                              type="button"
                              className="text-xs text-rose-700 underline hover:text-rose-900"
                              onClick={() => toggleCalls(run.runId)}
                              title="Requests that failed inside a property that otherwise succeeded — that data is missing for this run"
                            >
                              {callsOpen ? 'Hide' : `${callFailureCount} request${callFailureCount === 1 ? '' : 's'} failed`}
                            </button>
                          )}
                          {hasFailures && (
                            <button
                              type="button"
                              className="text-xs text-slate-600 underline hover:text-slate-900"
                              onClick={() => toggle(run.runId)}
                            >
                              {isOpen ? 'Hide' : `${run.failedAccounts.length} failed`}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {hasDeferred && deferredOpen && (
                      <tr className="bg-amber-50/40">
                        <td colSpan={9} className="px-4 py-3">
                          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                            Deferred accounts — still ingesting; re-run the same command later
                          </p>
                          <ul className="space-y-1">
                            {run.deferredAccounts.map((d, i) => (
                              <li key={`${d.accountId}-${i}`} className="text-sm text-slate-700">
                                <span className="font-medium text-slate-900">
                                  {d.accountName ?? d.accountId}
                                </span>
                                <span className="text-slate-500"> — {d.note ?? 'still backfilling'}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                    {callFailureCount > 0 && callsOpen && (
                      <tr className="bg-rose-50/40">
                        <td colSpan={9} className="px-4 py-3">
                          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                            Failed requests — this data did not land in this run
                          </p>
                          <ul className="space-y-1">
                            {run.callFailures.map((f, i) => (
                              <li key={`${f.request}-${i}`} className="text-sm text-slate-700">
                                <span className="font-medium text-slate-900">{f.request}</span>
                                <span className="text-slate-500"> — {f.error ?? 'error'}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                    {hasFailures && isOpen && (
                      <tr className="bg-rose-50/40">
                        <td colSpan={9} className="px-4 py-3">
                          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                            Failed accounts — re-check / re-backfill these
                          </p>
                          <ul className="space-y-1">
                            {run.failedAccounts.map((f, i) => (
                              <li key={`${f.accountId}-${i}`} className="text-sm text-slate-700">
                                <span className="font-medium text-slate-900">
                                  {f.accountName ?? f.accountId}
                                </span>
                                <span className="text-slate-500"> — {f.error ?? 'error'}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                    {hasSkipDetail && skipOpen && run.skipBreakdown && (
                      <tr className="bg-amber-50/40">
                        <td colSpan={9} className="px-4 py-3">
                          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                            Skipped accounts
                          </p>
                          <p className="mb-2 text-sm text-slate-600">
                            {formatSkipBreakdown(run.skipBreakdown)}
                          </p>
                          {/* Benign §3b shared-dedup: a single count, never itemised. */}
                          {run.skipBreakdown.sharedDedup > 0 && (
                            <p className="mb-2 text-sm text-slate-500">
                              {run.skipBreakdown.sharedDedup} covered by a shared ad account (pulled once) —
                              expected.
                            </p>
                          )}
                          {run.skippedAccounts.length > 0 ? (
                            <ul className="space-y-1">
                              {run.skippedAccounts.map((s, i) => {
                                const concerning = isConcerningSkipReason(s.reason);
                                return (
                                  <li
                                    key={`${s.accountId}-${i}`}
                                    className={`text-sm ${concerning ? 'text-amber-800' : 'text-slate-700'}`}
                                  >
                                    <span
                                      className={`font-medium ${concerning ? 'text-amber-900' : 'text-slate-900'}`}
                                    >
                                      {s.accountName ?? s.accountId}
                                    </span>
                                    <span className={concerning ? 'text-amber-700' : 'text-slate-500'}>
                                      {' '}
                                      — {skipReasonLabel(s.reason)}
                                      {concerning && ' ⚠'}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="text-sm text-slate-500">
                              All skips are benign shared-account dedup — nothing to action.
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
